/**
 * The server's state, named once.
 *
 * `lib/api.ts` stays exactly what it was — the typed client that knows the wire
 * shapes and normalises them tolerantly. This file is the layer above it: one
 * key per thing the server owns, and the query options that read it. Nothing
 * here fetches; every `queryFn` below is an `api.ts` function.
 *
 * Why it exists at all: the same fact was being read, held and re-reported by
 * three different components. The balance was the worst of them — the header
 * asked once and then had it handed back by whichever screen happened to
 * refresh it (`onBalance`), which meant money on screen was only as true as the
 * last callback that fired. A shared cache with one key made it a single fact
 * that every screen read and every mutation invalidated. Ruling R-0916-1
 * deleted the balance itself; the plan's share of its period is the fact that
 * inherited the problem, and `planQuery` is the one key that answers it.
 *
 * The `{ ok, ... }` unions from `api.ts` are unwrapped here rather than in the
 * components: a query either has data or it has an error, and a refusal the
 * screen cannot answer is an error. `UNAUTHENTICATED` is the one code that is
 * never a screen's business — it is the session, and it is handled once, by the
 * query client (`lib/query-client.ts`).
 */
import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import {
  fetchInvoices,
  fetchBlockedSenders,
  fetchHandleExists,
  fetchDoormanThread,
  fetchCurrentRules,
  fetchDataRequests,
  fetchDevDoors,
  fetchDevIdentities,
  fetchDoorSettings,
  fetchInbox,
  fetchMe,
  fetchMyKnocks,
  fetchMyWaiting,
  fetchHeldSpots,
  fetchOnboarding,
  fetchPlan,
  fetchProposals,
  fetchPublicDoor,
  fetchRecap,
  fetchSupportThreads,
  fetchThreads,
  typeahead,
  type InboxQuery,
  type InboxView,
  type OnboardingView,
} from "./api";

/**
 * The refusal a query throws when the server said no. The code is the server's
 * own (§5.0), so a screen can still answer the ones that are its business.
 */
export class ApiError extends Error {
  constructor(readonly code: string) {
    super(`nob: ${code}`);
    this.name = "ApiError";
  }
}

/** The session is gone. Handled in one place; never a screen's problem. */
export function isUnauthenticated(error: unknown): boolean {
  return error instanceof ApiError && error.code === "UNAUTHENTICATED";
}

type Ok<T> = { ok: true } & T;
type Refused = { ok: false; code: string };

/** A `{ ok }` union, as a value or as a throw. Queries deal in the latter. */
function unwrap<T>(result: Ok<T> | Refused): Ok<T> {
  if (!result.ok) throw new ApiError(result.code);
  return result;
}

/**
 * Every key the app caches under, in one place — so an invalidation is written
 * against the same name the read was, and a typo is a type error.
 */
export const qk = {
  me: () => ["me"] as const,
  /**
   * Where the onboarding run is (ruling C5). ONE key, read by the wizard that
   * runs the steps AND by the pinned row in the console that asks for the one
   * still owed — a second shape for "does this person owe the door step" would
   * be a second answer to it, and the two would disagree the moment one is
   * invalidated and the other is not.
   */
  onboarding: () => ["onboarding"] as const,
  rulesCurrent: () => ["rules", "current"] as const,
  recap: () => ["recap"] as const,
  doormanThread: () => ["doorman", "thread"] as const,
  /**
   * The view is the list's identity; the filter row narrows the same view, so
   * it belongs in the key too — otherwise applying a filter would show the
   * previous list's items while the narrowed one loaded.
   */
  inbox: (view: InboxView, filters?: InboxFilters) => ["inbox", view, filters ?? {}] as const,
  /**
   * The people this owner has shut out (FR-6.4).
   *
   * Under the `inbox` prefix on purpose, and not under `door`: blocking is a
   * recipient action taken on a knock, and the invalidation that follows one
   * (`["inbox"]`) has to reach this list too. The dialog that reads it happens
   * to live in the door settings, which is a matter of where the door's
   * paperwork is kept, not of what the fact is about.
   */
  blocked: () => ["inbox", "blocked"] as const,
  proposals: () => ["proposals"] as const,
  /** The PUBLIC door card — what a sender is shown, with no session. */
  door: (handle: string) => ["door", handle] as const,
  /**
   * The owner's own door settings (2026-08-03). A sibling of `door(handle)` and
   * deliberately not the same fact: that one is the card a STRANGER is served,
   * this one is the configuration behind it. A price change invalidates both,
   * because the mirror is supposed to move when the door does.
   */
  doorSettings: () => ["door", "settings"] as const,
  /**
   * The owner's plan. One key, read by the plan room AND by the line in `/you`
   * that leads to it — a second shape for "which plan" would be a second answer
   * to it, and this one is about money.
   */
  plan: () => ["plan"] as const,
  /** The invoices and receipts (wave 87), under the plan so a plan refresh takes them too. */
  invoices: () => ["plan", "invoices"] as const,
  /**
   * The help desk threads this person has opened (wave 14a). One key, read by
   * the Get help page and invalidated by the form that opens a thread — so a
   * message just sent appears in the list without the screen inventing a row.
   */
  support: () => ["support"] as const,
  /** The confirmations and exports waiting on this person in the console (wave 116). */
  dataRequests: () => ["data-requests"] as const,
  knock: (id: string) => ["knock", id] as const,
  /** The sender's own conversations — the `/knocks` room's whole list. */
  myKnocks: () => ["knocks", "mine"] as const,
  /**
   * The console's list: one thread per counterparty, at most one knock per
   * direction inside. Its OWN key, beside the two lists it is made of: an
   * action on a knock invalidates this too (`useReread`, the desk), so a row's
   * stage and its card cannot disagree for a refetch.
   */
  threads: () => ["threads"] as const,
  knockMessages: (id: string) => ["knock", id, "messages"] as const,
  knockStanding: (id: string) => ["knock", id, "standing"] as const,
  devIdentities: () => ["dev", "identities"] as const,
  devDoors: () => ["dev", "doors"] as const,
  /**
   * The typeahead (owner decision 2026-08-02; R-0904-3). The query text IS the
   * key: two different searches are two different lists, and a cached one is
   * what makes backspacing through a word feel instant instead of re-fetching
   * every prefix.
   */
  typeahead: (query: string) => ["directory", "typeahead", query] as const,
  /**
   * The held spots at your own door (WR FR-G9, PRD FR-6.3). One key: the
   * console reads every held spot it has, because FR-G9.4 keeps them all and
   * there is nothing to page through that the sidebar is not already showing.
   */
  heldSpots: () => ["waiting", "held"] as const,
  /**
   * Where the VIEWER is waiting (FR-G3). The mirror image of `heldSpots`, and
   * deliberately a different key: the knock dialog reads this to know which of
   * its rows already carry a spot of the reader's own.
   */
  myWaiting: () => ["waiting", "mine"] as const,
  /**
   * What is at one typed handle (WR FR-G1). Keyed by the handle because that is
   * the whole of the question, and lowercased because `@Maya` and `@maya` are
   * one X account and must not be two cache entries.
   */
  handleExists: (handle: string) => ["waiting", "handle", handle.toLowerCase()] as const,
};

export type InboxFilters = Omit<InboxQuery, "view" | "cursor">;

/** `null` is unauthenticated, not a failure — the sign-in screen, not an error. */
export const meQuery = () => queryOptions({ queryKey: qk.me(), queryFn: () => fetchMe() });

/**
 * The onboarding run (PRD FR-1.2 #7, ruling C5).
 *
 * A REFUSAL LANDS SOMEBODY IN THE CONSOLE, and this is the one query where that
 * matters enough to spell out. The run decides which screen `/` is, so a read
 * that throws would be an error page in the middle of signing up, and a read
 * that resolved to nothing would be a splash that never ends. Neither is a
 * state anybody can act on. So an unreadable run is a run with nothing left
 * owed: every flag false, no step, the shell — which is exactly what the
 * parser's own defaults say, said once more here where it is load-bearing.
 */
const NOTHING_OWED: OnboardingView = {
  onboarding: {
    email_pending: false,
    perk_pending: false,
    has_door: false,
    rules_pending: false,
    defer_rules: false,
  },
  starter: { interests: [], no_interests: "", filter_text: {}, notify_text: {} },
  // And nobody claimed to be waiting. A run that could not be read must not put
  // a number of people on the poster; the line is absent at zero.
  waiting: { count: 0 },
  // And no attestation, for the same reason: a sentence this side invented is
  // one nobody could prove was shown. The step is unanswerable without it,
  // which is the correct failure — the console is what an unreadable run lands
  // on, and the step is not on the way there.
  age_attestation: "",
};

export const onboardingQuery = () =>
  queryOptions({
    queryKey: qk.onboarding(),
    queryFn: async () => {
      const r = await fetchOnboarding();
      return r.ok ? r.view : NOTHING_OWED;
    },
  });

// `walletQuery` stood here, holding the balance once for the header, the wallet
// room and the knock's funding block. Ruling R-0916-1 deleted all three. What
// every one of those surfaces reads instead is `planQuery` below — one key, and
// a share of a period rather than a figure.

export const currentRulesQuery = () =>
  queryOptions({
    queryKey: qk.rulesCurrent(),
    queryFn: async () => unwrap(await fetchCurrentRules()).current,
  });

/**
 * What this person has written to us, and where a reply will go (wave 14a).
 *
 * `unwrap`ped like the rest: a refusal this screen cannot answer is an error,
 * and the only one it could answer — no session — is handled once by the query
 * client. It carries no message bodies, by contract and by leak test.
 */
export const supportQuery = () =>
  queryOptions({
    queryKey: qk.support(),
    queryFn: async () => unwrap(await fetchSupportThreads()),
  });

/**
 * What an erasure or an export somebody asked us for needs of this person: a
 * confirmation to click and a file to download (wave 116). Empty almost always.
 */
export const dataRequestsQuery = () =>
  queryOptions({
    queryKey: qk.dataRequests(),
    queryFn: async () => unwrap(await fetchDataRequests()),
  });

/** The owner's conversation with their doorman (2026-08-03), plus the meter. */
export const doormanThreadQuery = () =>
  queryOptions({ queryKey: qk.doormanThread(), queryFn: () => fetchDoormanThread() });

/** Today's would-be briefing (FR-7.3). A suppressed morning is data, not an error. */
export const recapQuery = () =>
  queryOptions({
    queryKey: qk.recap(),
    queryFn: async () => unwrap(await fetchRecap("preview")).recap,
  });

/** The proposals waiting on this door (M15, FR-8.3) — an offer, never urgent. */
export const proposalsQuery = () =>
  queryOptions({
    queryKey: qk.proposals(),
    queryFn: async () => unwrap(await fetchProposals()).proposals,
  });

/**
 * The sender's conversations (the `/knocks` room). Cached like any other list,
 * and deliberately NOT keyed by anything: there is one list per session, and the
 * session is what the query client is cleared on at sign-out.
 */
export const myKnocksQuery = () =>
  queryOptions({
    queryKey: qk.myKnocks(),
    queryFn: async () => unwrap(await fetchMyKnocks()).knocks,
    /**
     * Always re-read on entry, unlike the app's 30-second default. Every row
     * here moves for reasons that happen in ANOTHER room — a turn taken in a
     * thread, a knock started at a door, a hearing scored while the tab was
     * shut — so a list that could be half a minute behind would be a list
     * missing the conversation somebody just had. The cached copy still renders
     * first, so this costs a refresh and never a spinner.
     */
    staleTime: 0,
  });

/**
 * The console's list — one thread per counterparty. `staleTime: 0` for the same
 * reason `myKnocksQuery` has it: every row moves for reasons that happen in
 * another room or another browser, and a list half a minute behind is a list
 * missing the conversation somebody just had.
 */
export const threadsQuery = () =>
  queryOptions({
    queryKey: qk.threads(),
    queryFn: async () => unwrap(await fetchThreads()).threads,
    staleTime: 0,
  });

export const doorQuery = (handle: string) =>
  queryOptions({
    queryKey: qk.door(handle),
    queryFn: async () => unwrap(await fetchPublicDoor(handle)).door,
  });

/**
 * The owner's own door settings. `DOOR_NOT_FOUND` is an error like any other
 * here — a viewer with no door simply never enables this query, and a settings
 * panel that rendered defaults for a door nobody owns would be a form that
 * cannot save.
 */
export const doorSettingsQuery = () =>
  queryOptions({
    queryKey: qk.doorSettings(),
    queryFn: async () => unwrap(await fetchDoorSettings()).settings,
  });

/**
 * The people this owner has shut out (FR-6.4). Read by the Blocked-users row in
 * the door settings, which shows the count, and by the dialog behind it, which
 * shows the list — one read, so the number on the row and the rows in the
 * dialog are the same fact rather than two.
 */
export const blockedQuery = () =>
  queryOptions({
    queryKey: qk.blocked(),
    queryFn: async () => unwrap(await fetchBlockedSenders()).blocked,
  });

/**
 * The accounts the doorman answers from. A linked account is only ever proved
 * by this read — the linking handshake finishes in the OTHER app (or in a
 * mailbox), so nothing on screen may claim a link the server has not confirmed.
 */
/**
 * The plan (docs/plans.md §14.3). `PLANS_DISABLED` is not an error and must not
 * reach the shared error handler: with the flag off the server answers 404 and
 * there is simply no plan lane, so the read resolves to null and every surface
 * that reads it renders nothing. Anything else throws, as it should.
 */
export const planQuery = () =>
  queryOptions({
    queryKey: qk.plan(),
    queryFn: async () => {
      const r = await fetchPlan();
      if (!r.ok && r.code === "PLANS_DISABLED") return null;
      return unwrap(r).view;
    },
  });

/**
 * THE INVOICES AND RECEIPTS (wave 87), a page of 24 at a time, newest first.
 * "More" is `fetchNextPage`; the cursor is the server's.
 */
export const invoicesQuery = () =>
  infiniteQueryOptions({
    queryKey: qk.invoices(),
    queryFn: async ({ pageParam }) => unwrap(await fetchInvoices(pageParam)),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor,
  });

/**
 * One page at a time, because the inbox is a cursor (FR-6). "More" is
 * `fetchNextPage`, so the accumulated list is the cache's rather than a
 * component's — which is what lets a view switch drop it without a race.
 */
export const inboxQuery = (view: InboxView, filters: InboxFilters) =>
  infiniteQueryOptions({
    queryKey: qk.inbox(view, filters),
    queryFn: async ({ pageParam }) =>
      unwrap(await fetchInbox({ view, ...filters, ...(pageParam ? { cursor: pageParam } : {}) }))
        .page,
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor,
  });

/**
 * The dev affordances (local stacks only). Both `api.ts` functions answer with
 * an empty list against a real identity port rather than throwing, which is the
 * behaviour these screens want: no stand-in, nothing to show, no error.
 */
export const devIdentitiesQuery = () =>
  queryOptions({ queryKey: qk.devIdentities(), queryFn: () => fetchDevIdentities() });

export const devDoorsQuery = () =>
  queryOptions({ queryKey: qk.devDoors(), queryFn: () => fetchDevDoors() });

/**
 * The listed doors matching what somebody has typed (owner decision 2026-08-02).
 *
 * `staleTime` is 30s because the answer barely moves — a door's name and price
 * are not live figures — and because the same prefixes come back constantly while
 * a person types, backspaces and types again. A refusal the box cannot act on
 * (a rate limit, a dropped session) is an error like any other; the caller shows
 * the input and no list, which is the same thing it shows before anyone types.
 */
/**
 * Held spots, folded into the console list beside real knocks.
 *
 * `staleTime` matches `myKnocksQuery`'s reasoning rather than the board's: a
 * held spot disappears when its registrant finally knocks, which happens in
 * somebody else's browser, so a list half a minute behind would show one person
 * twice — once as a held spot and once as the knock it became.
 */
export const heldSpotsQuery = () =>
  queryOptions({
    queryKey: qk.heldSpots(),
    queryFn: () => fetchHeldSpots(),
    staleTime: 0,
  });

/**
 * The viewer's own registrations, for the knock dialog's rows.
 *
 * `staleTime: 0` for the same reason `heldSpotsQuery` has it: a registration
 * ends when the handle's owner finally opens a door, and a cached copy would
 * keep offering a spot in a queue that no longer exists.
 */
export const myWaitingQuery = () =>
  queryOptions({
    queryKey: qk.myWaiting(),
    queryFn: () => fetchMyWaiting(),
    staleTime: 0,
  });

export const typeaheadQuery = (query: string) =>
  queryOptions({
    queryKey: qk.typeahead(query),
    queryFn: async () => unwrap(await typeahead(query)).rows,
    staleTime: 30_000,
  });

/**
 * What is at a handle somebody TYPED — the third road out of the knock dialog,
 * and, since ruling R-0910-2, the FREE one.
 *
 * NOT A SEARCH, AND STRUCTURALLY SO. It is `GET /door/{handle}/exists`, which
 * answers about that one account out of Nob's own records, its miss cache, or
 * X's keyless embed endpoint. No query text reaches the directory and none
 * reaches the board, which is the rule a typed `@handle` has always been under —
 * and it is what lets the dialog offer a spot at a handle that has neither a
 * door NOR a queue, since a handle with no queue is exactly the one the board
 * cannot know about.
 *
 * IT COSTS NOTHING, AND THAT IS WHY IT MAY RUN ON A KEYSTROKE. It used to be
 * `GET /waiting/{handle}`, which resolved the handle against X's PAID user
 * lookup — about a cent, on our account, every time somebody typed an `@` and
 * kept going. The paid road still exists and is `lookUpHandleOnX`, which is
 * reached only by pressing a button that says so.
 *
 * Failures answer `exists: null` rather than throwing: a handle nobody holds is
 * an ordinary answer to an ordinary question, and X not answering is a third
 * state that must never be drawn as the second.
 */
export const handleExistsQuery = (handle: string) =>
  queryOptions({
    queryKey: qk.handleExists(handle),
    queryFn: () => fetchHandleExists(handle),
    staleTime: 30_000,
  });
